import React, { useEffect, useMemo, useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';

function formatCount(value) {
  const count = Number(value || 0);
  return `${count} recipient${count === 1 ? '' : 's'}`;
}

function toDate(value) {
  if (!value) return null;
  const date = value?.toDate?.() || value;
  if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatSentAt(value) {
  const date = toDate(value);
  if (!date) return 'Date unavailable';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export default function InboxBroadcastsCard({
  teamId,
  isAdmin,
  refreshKey = 0,
  onCompose,
  onOpenBroadcast,
}) {
  const [loading, setLoading] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const PAGE_SIZE = 4;
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!teamId || !isAdmin) return undefined;
    setLoading(true);

    const unsubscribe = safeOnSnapshot(query(
      collection(db, 'teams', teamId, 'inboxBroadcasts'),
      orderBy('createdAt', 'desc')
    ), (snap) => {
      setBroadcasts(snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })));
      setPage(0);
      setLoading(false);
    }, () => {
      setLoading(false);
    }, { label: 'inbox-broadcasts', teamId });

    return () => {
      unsubscribe();
    };
  }, [teamId, isAdmin, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(broadcasts.length / PAGE_SIZE));
  const startIndex = page * PAGE_SIZE;
  const visible = broadcasts.slice(startIndex, startIndex + PAGE_SIZE);
  const summary = useMemo(() => ({
    total: broadcasts.length,
    recipients: broadcasts.reduce((sum, item) => sum + Number(item?.resolvedRecipientCount || 0), 0),
  }), [broadcasts]);

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Inbox Broadcasts</span>
      </div>

      <div className="card-body">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'end' }}>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            {loading ? 'Loading…' : (
              <>
                <strong>{summary.total}</strong> broadcasts • <strong>{summary.recipients}</strong> total recipients
              </>
            )}
          </div>

          {isAdmin && (
            <button className="btn btn--primary" onClick={() => onCompose?.()} style={{ whiteSpace: 'nowrap' }}>
              New Inbox Message
            </button>
          )}
        </div>

        {broadcasts.length > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button className="btn btn--ghost" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page === 0}>
              ‹
            </button>
            <span className="text-muted" style={{ alignSelf: 'center' }}>
              {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, broadcasts.length)} of {broadcasts.length}
            </span>
            <button className="btn btn--ghost" onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))} disabled={page >= totalPages - 1}>
              ›
            </button>
          </div>
        )}

        {!loading && visible.length === 0 && (
          <p className="text-muted" style={{ marginTop: '0.75rem' }}>
            No inbox broadcasts have been sent yet.
          </p>
        )}

        <div className="list inbox-broadcast-list">
          {visible.map((broadcast) => {
            const sentAtLabel = formatSentAt(broadcast?.createdAt);
            const deliverySummary = broadcast?.deliverySummary || {};
            const status = String(broadcast?.status || 'queued');
            const sender = broadcast?.createdByName || 'Admin';
            const deliveredLabel = status === 'completed' ? `${deliverySummary?.delivered || 0} delivered` : status;
            return (
              <button
                key={broadcast.id}
                type="button"
                className="list-row inbox-broadcast-row"
                onClick={() => onOpenBroadcast?.(broadcast)}
                aria-label={`Open inbox broadcast from ${sender} sent ${sentAtLabel}`}
              >
                <div className="inbox-broadcast-row__main">
                  <div className="inbox-broadcast-row__title">
                    From {sender}
                  </div>
                  <div className="text-muted inbox-broadcast-row__meta">
                    {sentAtLabel}
                  </div>
                </div>
                <div className="inbox-broadcast-row__stat" title="Audience">
                  <span>Audience</span>
                  <strong>{formatCount(broadcast?.resolvedRecipientCount)}</strong>
                </div>
                <div className="inbox-broadcast-row__stat" title="Delivery status">
                  <span>Status</span>
                  <strong>{deliveredLabel}</strong>
                </div>
              </button>
            );
          })}
        </div>

        {!loading && broadcasts.length > 0 && (
          <p className="text-muted inbox-broadcast-help">
            Open a broadcast to view the message and delivery details. Members can reply in the 1:1 thread it creates.
          </p>
        )}
      </div>
    </div>
  );
}
